import React from "react";

type FieldBaseProps = {
    className?: string;
    classInput?: string;
    classLabel?: string;
    label?: string;
    innerLabel?: string;
    error?: string;
};

type InputFieldProps = FieldBaseProps &
    React.InputHTMLAttributes<HTMLInputElement> & {
        as?: "input";
    };

type SelectFieldProps = FieldBaseProps &
    React.SelectHTMLAttributes<HTMLSelectElement> & {
        as: "select";
        children: React.ReactNode;
    };

type TextareaFieldProps = FieldBaseProps &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
        as: "textarea";
    };

type FieldProps = InputFieldProps | SelectFieldProps | TextareaFieldProps;

const baseClasses =
    "w-full border border-s-stroke2 text-body-2 text-t-primary outline-none transition-colors hover:border-s-highlight focus:border-s-highlight placeholder:text-t-secondary/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-s-stroke2 bg-b-surface2";

const errorClasses =
    "!border-red-500 hover:!border-red-500 focus:!border-red-500";

const selectChevron =
    "appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%237b7b7b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_1rem_center] pr-10 cursor-pointer";

const Field = (props: FieldProps) => {
    const {
        className,
        classInput,
        classLabel,
        label,
        innerLabel,
        error,
        as = "input",
        ...rest
    } = props;

    const hasInnerLabel = !!innerLabel;
    const hasLabel = !!label && !hasInnerLabel;

    const shapeClasses =
        as === "textarea"
            ? `h-24 px-4.5 py-3.5 rounded-3xl resize-none ${hasInnerLabel ? "pl-6.5" : ""}`
            : `h-12 px-4.5 rounded-full ${hasInnerLabel ? "pl-6.5" : ""}`;

    const inputClassName = `${baseClasses} ${shapeClasses} ${error ? errorClasses : ""} ${as === "select" ? selectChevron : ""
        } ${classInput || ""}`;

    const renderElement = () => {
        if (as === "textarea") {
            return (
                <textarea
                    className={inputClassName}
                    {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                />
            );
        }
        if (as === "select") {
            return (
                <select
                    className={inputClassName}
                    {...(rest as React.SelectHTMLAttributes<HTMLSelectElement>)}
                >
                    {(props as SelectFieldProps).children}
                </select>
            );
        }
        return (
            <input
                className={inputClassName}
                type={(rest as React.InputHTMLAttributes<HTMLInputElement>).type || "text"}
                {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
            />
        );
    };

    return (
        <div className={`${hasInnerLabel ? "pt-1.5" : ""} ${className || ""}`}>
            {hasLabel && (
                <div className="flex items-center mb-2">
                    <div className={`text-button ${classLabel || ""}`}>{label}</div>
                </div>
            )}
            <div className="relative">
                {hasInnerLabel && (
                    <div className={`absolute -top-2.5 left-6 px-1 text-caption pointer-events-none z-1 ${classLabel || "bg-b-surface2"}`}>
                        {innerLabel}
                    </div>
                )}
                {renderElement()}
            </div>
            {error && (
                <div className="mt-2 text-caption text-red-500">{error}</div>
            )}
        </div>
    );
};

export default Field;
